// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Stage, Layer, Text, Line } from "react-konva";
import "konva/lib/shapes/Line";
import "konva/lib/shapes/Text";
import ResizeObserver from "resize-observer-polyfill";

import { DashboardPanel } from "types";
import { IAction } from "actions";
import { isSet, formatTimestamp } from "utils";
import { formatPowerValue, getPanelSelection } from "guiutils";
import { IRegulation, ITariffCircuit } from "parameter";
import { IState } from "states/states";
import { IControlData } from "states/datastates";

import cssContent from "content.scss";
import MonitorGraphLegend from "./MonitorGraphLegend";

const X_BORDER_LEFT = 20;
const X_BORDER_RIGHT = 49; // needed for text on the right
const Y_BORDER_BOTTOM = 20;
const Y_BORDER_TOP = 13;
const Y_VIEW_RESERVE_LEGEND = 1.3;
const Y_VIEW_RESERVE_NO_LEGEND = 1.1;
const TEXT_SPACER = 5;
const TEXT_WIDTH = 100; // set this large enough for the largest text
const TEXT_HEIGHT = 10;

interface IMonitorGraphExtProps {
  dashboardIndex?: number;
}

type IMonitorGraphProps = {
  showLegend?: boolean;
  isAnnotated?: boolean;
  curPower: number;
  controlParameter: IRegulation | undefined;
  tariffParameter: ITariffCircuit | undefined;
  controlCircuitIndex: number | undefined;
  data: IControlData | undefined;
};

const mapStateToProps = (state: IState, ownProps: IMonitorGraphExtProps) => {
  const { index, selection } = getPanelSelection(
    state,
    DashboardPanel.MONITOR,
    ownProps.dashboardIndex,
  );
  let controlCircuitIndex = state.parameter?.regulation.findIndex(
    (curItem, curIndex) => (curItem.id ?? curIndex) === selection?.controlCircuitId,
  );
  if (isSet(controlCircuitIndex) && controlCircuitIndex < 0) {
    controlCircuitIndex = undefined;
  }
  if (!isSet(controlCircuitIndex) && state.parameter && state.parameter.regulation.length > 0) {
    controlCircuitIndex = 0; // make default selection
  }

  const controlParameter = isSet(controlCircuitIndex)
    ? state.parameter?.regulation[controlCircuitIndex]
    : undefined;
  const tlId = controlParameter?.tariffline;
  const tlIndex = state.parameter?.tariffline.findIndex(
    (curItem, curIndex) => tlId === (curItem.id ?? curIndex),
  );
  const props: IMonitorGraphProps = {
    data: isSet(controlCircuitIndex) ? state.data.controlcircuit[controlCircuitIndex] : undefined,
    showLegend: true,
    isAnnotated: true,
    curPower: state.data.tariffcircuit[tlIndex!]?.power ?? NaN,
    controlParameter,
    tariffParameter: state.parameter?.tariffline[tlIndex!],
    controlCircuitIndex,
  };
  return props;
};

interface IMonitorGraphDispatch {}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IMonitorGraphDispatch = {};
  return dispatchProps;
};

const messages = defineMessages({
  msg_label_target: {
    id: "Target",
  },
  msg_label_trend: {
    id: "Trend",
  },
});

class MonitorGraphComponent extends React.Component<
  IMonitorGraphProps & IMonitorGraphExtProps & IMonitorGraphDispatch & WrappedComponentProps
> {
  private resizeObserver: ResizeObserver | undefined;
  private readonly compRef = React.createRef<HTMLDivElement>();
  private width = 1; // initial value, will be changed with resize event
  private height = 1; // initial value, will be changed with resize event

  private xScale = 1;
  private yScale = 1;
  private yMin = 0;

  componentDidMount() {
    this.resizeObserver = new ResizeObserver((elem: ResizeObserverEntry[]) => {
      this.width = elem[0].contentRect.width;
      this.height = elem[0].contentRect.height;
      this.forceUpdate();
    });
    if (this.compRef.current) this.resizeObserver.observe(this.compRef.current);
    else console.warn("did not register monitor size observer");
  }

  componentWillUnmount() {
    this.resizeObserver?.disconnect();
  }

  public render() {
    // check for existing data
    if (
      !isSet(this.props.controlCircuitIndex) ||
      !isSet(this.props.tariffParameter) ||
      !isSet(this.props.data?.targetValue) ||
      !this.props.tariffParameter?.regulationPeriod ||
      !this.props.data?.values.length
    ) {
      return (
        <div className="monitorgraphitem nodata" ref={this.compRef}>
          <FormattedMessage id="No data available." />
        </div>
      );
    }

    this.rescale();

    return (
      <div className="monitorgraphitem" ref={this.compRef}>
        {
          // optionally, draw legend
          this.props.showLegend && (
            <MonitorGraphLegend
              controlCircuitIndex={this.props.controlCircuitIndex}
              isDashboard={isSet(this.props.dashboardIndex)}
            />
          )
        }
        <Stage width={this.width} height={this.height}>
          <Layer>
            {
              // paint the graph first, to overlay the other lines afterwards
              this.renderGraph()
            }
            {
              // draw trend line with caption
              this.renderTrendLine()
            }
            {
              // draw axis and limit line
              this.renderAxis()
            }
            {
              // draw current time line and state current values
              this.renderTimeLine()
            }
          </Layer>
        </Stage>
      </div>
    );
  }

  private rescale() {
    // scale x axis
    this.xScale =
      (this.width - X_BORDER_LEFT - X_BORDER_RIGHT) / this.props.tariffParameter!.regulationPeriod;

    // scale y axis
    const minYValue = Math.min(
      this.props.data!.values.reduce((prev, cur) => (prev.energy < cur.energy ? prev : cur)).energy,
      this.props.data!.targetValue,
      this.getTrendValue(),
      0,
    );
    const maxYValue = Math.max(
      this.props.data!.values.reduce((prev, cur) => (prev.energy > cur.energy ? prev : cur)).energy,
      this.props.data!.targetValue,
      this.getTrendValue(),
    );

    // determine scaline
    // do we have enough data?
    if (maxYValue - minYValue > 0) {
      this.yScale =
        (this.height - Y_BORDER_BOTTOM - Y_BORDER_TOP) /
        ((maxYValue - minYValue) *
          (this.props.showLegend ? Y_VIEW_RESERVE_LEGEND : Y_VIEW_RESERVE_NO_LEGEND));
      this.yMin = minYValue;
    }
    // keep old scale otherwise
  }

  private timeToX(time: number) {
    return X_BORDER_LEFT + time * this.xScale;
  }
  private valueToY(value: number) {
    return this.height - Y_BORDER_BOTTOM - (value - this.yMin) * this.yScale;
  }

  private renderAxis() {
    const regPeriod = this.props.tariffParameter!.regulationPeriod;
    const xAxis = [this.timeToX(0), this.valueToY(0), this.timeToX(regPeriod), this.valueToY(0)];
    const rightAxis = [
      this.timeToX(regPeriod),
      Y_BORDER_TOP,
      this.timeToX(regPeriod),
      this.valueToY(0),
    ];
    const targetLine = [
      this.timeToX(0),
      this.valueToY(0),
      this.timeToX(regPeriod),
      this.valueToY(this.props.data!.targetValue),
    ];

    return (
      <>
        <Line stroke={cssContent.GRAPH_COLOR_AXIS} points={xAxis} />
        <Line stroke={cssContent.GRAPH_COLOR_AXIS} points={rightAxis} />
        {/* unit caption */}
        <Text
          x={this.timeToX(regPeriod) - TEXT_WIDTH / 2}
          y={0}
          width={TEXT_WIDTH}
          align="center"
          verticalAlign="top"
          fontStyle="bold"
          text={this.props.tariffParameter!.unit}
        />
        {/* period time captions */}
        <Text
          x={this.timeToX(0) - TEXT_WIDTH / 2}
          y={this.valueToY(0) + TEXT_SPACER}
          width={TEXT_WIDTH}
          align="center"
          verticalAlign="top"
          text={formatTimestamp(0)}
        />
        <Text
          x={this.timeToX(regPeriod) - TEXT_WIDTH / 2}
          y={this.valueToY(0) + TEXT_SPACER}
          width={TEXT_WIDTH}
          align="center"
          verticalAlign="top"
          text={formatTimestamp(regPeriod)}
        />
        {/* target line */}
        <Line
          points={targetLine}
          stroke={cssContent.GRAPH_COLOR_TARGET}
          strokeWidth={parseInt(cssContent.GRAPH_TARGET_LINE_WIDTH)}
        />
        {/* target value caption */}
        <Text
          x={this.timeToX(regPeriod) + TEXT_SPACER}
          y={this.valueToY(this.props.data!.targetValue) - TEXT_HEIGHT / 2}
          align="left"
          verticalAlign="middle"
          fill={cssContent.GRAPH_COLOR_TARGET}
          text={
            (this.props.isAnnotated
              ? this.props.intl.formatMessage(messages.msg_label_target) + "\n"
              : "") +
            formatPowerValue(this.props.data!.targetValue, this.props.tariffParameter?.unit)
          }
        />
      </>
    );
  }

  private renderGraph() {
    const regPeriod = this.props.tariffParameter!.regulationPeriod;
    // some initial values
    let oldTime = 0;
    let oldValue = 0;
    let oldTargetValue = 0;

    // loop through the data
    const energyItems = this.props.data!.values.map((curVal) => {
      const curTargetValue = (this.props.data!.targetValue * curVal.timestamp) / regPeriod;
      // single color polygon?
      if (
        (curVal.energy <= curTargetValue && curTargetValue >= 0) ||
        (curVal.energy >= curTargetValue && curTargetValue < 0) ||
        curVal.isEstimate
      ) {
        // define area
        const points = [
          this.timeToX(oldTime),
          this.valueToY(0),
          this.timeToX(oldTime),
          this.valueToY(oldValue),
          this.timeToX(curVal.timestamp),
          this.valueToY(curVal.energy),
          this.timeToX(curVal.timestamp),
          this.valueToY(0),
        ];
        oldTime = curVal.timestamp;
        oldValue = curVal.energy;
        oldTargetValue = curTargetValue;

        // draw filling
        const color = curVal.isEstimate
          ? cssContent.GRAPH_COLOR_ESTIMATE
          : curTargetValue >= 0
          ? cssContent.GRAPH_COLOR
          : cssContent.GRAPH_COLOR_OVERSHOOT;
        return (
          <Line key={curVal.timestamp} points={points} fill={color} stroke={color} closed={true} />
        );
      }

      // 2-color polygon
      // area below target line
      const pointsBelow = [
        this.timeToX(oldTime),
        this.valueToY(0),
        this.timeToX(oldTime),
        this.valueToY(oldTargetValue),
        this.timeToX(curVal.timestamp),
        this.valueToY(curTargetValue),
        this.timeToX(curVal.timestamp),
        this.valueToY(0),
      ];
      const colorBelow =
        curTargetValue >= 0 ? cssContent.GRAPH_COLOR : cssContent.GRAPH_COLOR_OVERSHOOT;
      // area above target line
      const pointsAbove = [
        this.timeToX(oldTime),
        this.valueToY(oldTargetValue),
        this.timeToX(oldTime),
        this.valueToY(oldValue),
        this.timeToX(curVal.timestamp),
        this.valueToY(curVal.energy),
        this.timeToX(curVal.timestamp),
        this.valueToY(curTargetValue),
      ];
      const colorAbove =
        curTargetValue >= 0 ? cssContent.GRAPH_COLOR_OVERSHOOT : cssContent.GRAPH_COLOR;

      oldTime = curVal.timestamp;
      oldValue = curVal.energy;
      oldTargetValue = curTargetValue;

      return (
        <React.Fragment key={curVal.timestamp}>
          <Line points={pointsBelow} fill={colorBelow} stroke={colorBelow} closed={true} />
          <Line points={pointsAbove} fill={colorAbove} stroke={colorAbove} closed={true} />
        </React.Fragment>
      );
    });

    return energyItems;
  }

  private renderTimeLine() {
    // determine current data point
    const curVal = this.props.data!.values[this.props.data!.values.length - 1];
    const currentTargetValue =
      (this.props.data!.targetValue * curVal.timestamp) /
      this.props.tariffParameter!.regulationPeriod;

    // draw current time line
    const xPos = this.timeToX(curVal.timestamp);
    const points = [xPos, Y_BORDER_TOP, xPos, this.height - Y_BORDER_BOTTOM + TEXT_SPACER / 2];

    return (
      <>
        <Line stroke={cssContent.GRAPH_COLOR_CUR_TIMETAMP} points={points} />
        {this.props.isAnnotated && (
          <>
            {/* current time stamp text */}
            <Text
              x={xPos - TEXT_WIDTH / 2}
              y={this.valueToY(0) + TEXT_SPACER}
              width={TEXT_WIDTH}
              align="center"
              verticalAlign="top"
              fill={cssContent.GRAPH_COLOR_CUR_TIMETAMP}
              text={formatTimestamp(curVal.timestamp)}
            />
            {/* current energy text */}
            <Text
              x={xPos + TEXT_SPACER}
              y={this.valueToY(curVal.energy)}
              align="left"
              verticalAlign="top"
              text={formatPowerValue(curVal.energy, this.props.tariffParameter?.unit)}
            />
            {/* proportional target energy text */}
            <Text
              x={xPos + TEXT_SPACER}
              y={this.valueToY(currentTargetValue)}
              align="left"
              verticalAlign="top"
              fill={cssContent.GRAPH_COLOR_TARGET}
              text={formatPowerValue(currentTargetValue, this.props.tariffParameter?.unit)}
            />
          </>
        )}
      </>
    );
  }

  private getTrendValue() {
    const regPeriod = this.props.tariffParameter!.regulationPeriod;
    const curVal = this.props.data!.values[this.props.data!.values.length - 1];
    const trendValue = curVal.energy + this.props.curPower * (1 - curVal.timestamp / regPeriod);
    return trendValue;
  }

  private renderTrendLine() {
    // derive trend value
    const regPeriod = this.props.tariffParameter!.regulationPeriod;
    const curVal = this.props.data!.values[this.props.data!.values.length - 1];
    const trendValue = this.getTrendValue();
    const line = [
      this.timeToX(curVal.timestamp),
      this.valueToY(curVal.energy),
      this.timeToX(regPeriod),
      this.valueToY(trendValue),
    ];

    // we can only draw a trend line if we are still in the viewable graph area
    if (curVal.timestamp >= regPeriod) return <></>;

    return (
      <>
        <Line stroke={cssContent.GRAPH_COLOR_TREND} points={line} dash={[1, 3]} />
        {/* trend value caption */}
        <Text
          x={this.timeToX(regPeriod) + TEXT_SPACER}
          y={this.valueToY(trendValue) - TEXT_HEIGHT / 2}
          align="left"
          verticalAlign="middle"
          fill={cssContent.GRAPH_COLOR_TREND}
          text={
            (this.props.isAnnotated
              ? this.props.intl.formatMessage(messages.msg_label_trend) + "\n"
              : "") + formatPowerValue(trendValue, this.props.tariffParameter?.unit)
          }
        />
      </>
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(MonitorGraphComponent));
